import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiErrorReason, apiStatus, fetchIgnored } from './api';
import {
  IGNORED_REASON_ORDER,
  defaultIgnoredRange,
  formatTs,
  ignoredQuery,
  ignoredReasonLabel,
  ignoredRowLabel,
  rangeError
} from './advisors';
import AssignAdvisorModal from './AssignAdvisorModal';
import type { AssignStudent } from './AssignAdvisorModal';
import { SHARED_STYLES } from '../styles';
import type { DayRange } from './advisors';
import type { IgnoredList } from './types';
import type { Notify } from './AdvisorsPanel';

interface Props {
  notify: Notify;
  initialRange?: DayRange;
  // Reports an applied range so the page can keep it in the URL.
  onRangeChange?: (range: DayRange) => void;
  // Owned by the panel so the marker survives toggling views while the Advisors tab is open.
  assigned: ReadonlySet<string>;
  onAssigned: (studentUuid: string) => void;
}

const { MUTED, PRIMARY_BTN, SECONDARY_BTN, FIELD } = SHARED_STYLES;

const COLS = ['When', 'Student', 'Tagged by', 'Why nobody was assigned', ''];
// Rows whose tag found an advisor already: Reassign is the path there, not Assign.
const ALREADY_ASSIGNED_REASON = 'ignored_already_assigned';
const EMPTY: IgnoredList = { rows: [], summary: { by_reason: {}, by_author: [] }, next_cursor: 0 };

// Notes whose tag did not assign an advisor (REQ 51), by IST day range.
function IgnoredView({ notify, initialRange, onRangeChange, assigned, onAssigned }: Props) {
  const [draft, setDraft] = useState<DayRange>(initialRange || defaultIgnoredRange());
  const [applied, setApplied] = useState<DayRange>(draft);
  const [data, setData] = useState<IgnoredList>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<{ forbidden: boolean; reason: string } | null>(null);
  const [assignTarget, setAssignTarget] = useState<AssignStudent | null>(null);
  const done = (uuid: string) => assigned.has(uuid);
  const loadSeq = useRef(0);
  const draftError = rangeError(draft);

  const load = useCallback(
    (range: DayRange) => {
      loadSeq.current += 1;
      const seq = loadSeq.current;
      setLoading(true);
      return fetchIgnored(ignoredQuery(range))
        .then((d) => {
          if (seq !== loadSeq.current) return;
          setData(d);
          setLoadError(null);
        })
        .catch((e: unknown) => {
          if (seq !== loadSeq.current) return;
          const forbidden = apiStatus(e) === 403;
          const reason = forbidden
            ? 'Super user or sales manager only'
            : apiErrorReason(e, 'Failed to load the list');
          // Drop the previous range's rows so they never sit under the new dates.
          setData(EMPTY);
          setLoadError({ forbidden, reason });
          if (!forbidden) notify('error', reason);
        })
        .finally(() => {
          if (seq === loadSeq.current) setLoading(false);
        });
    },
    [notify]
  );

  useEffect(() => {
    load(applied);
  }, [load, applied]);

  const loadMore = () => {
    if (!data.next_cursor || loadingMore) return;
    const seq = loadSeq.current;
    setLoadingMore(true);
    fetchIgnored(ignoredQuery(applied, data.next_cursor))
      .then((d) => {
        if (seq !== loadSeq.current) return;
        setData((prev) => ({
          ...prev,
          rows: [...prev.rows, ...d.rows],
          next_cursor: d.next_cursor
        }));
      })
      .catch((e: unknown) => notify('error', apiErrorReason(e, 'Failed to load more')))
      .finally(() => setLoadingMore(false));
  };

  const apply = (range: DayRange) => {
    setDraft(range);
    setApplied(range);
    onRangeChange?.(range);
  };

  const { by_reason: byReason, by_author: byAuthor } = data.summary;

  return (
    <div aria-label="Not assigned">
      <form
        className="px-5 py-3 flex flex-wrap items-end gap-3 border-b border-line"
        onSubmit={(e) => {
          e.preventDefault();
          if (!draftError) apply({ ...draft });
        }}
      >
        <label htmlFor="ignored-from" className="text-xs" style={MUTED}>
          From (IST)
          <input
            id="ignored-from"
            type="date"
            value={draft.from}
            onChange={(e) => setDraft({ ...draft, from: e.target.value })}
            className="block text-sm"
            style={FIELD}
          />
        </label>
        <label htmlFor="ignored-to" className="text-xs" style={MUTED}>
          To (IST)
          <input
            id="ignored-to"
            type="date"
            value={draft.to}
            onChange={(e) => setDraft({ ...draft, to: e.target.value })}
            className="block text-sm"
            style={FIELD}
          />
        </label>
        <button
          type="submit"
          disabled={!!draftError}
          className="rounded-md px-4 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
          style={PRIMARY_BTN}
        >
          Apply
        </button>
        <button
          type="button"
          onClick={() => {
            apply(defaultIgnoredRange());
          }}
          className="rounded-md px-4 py-1.5 text-sm font-medium"
          style={SECONDARY_BTN}
        >
          Last 7 days
        </button>
        {draftError ? (
          <span role="alert" className="text-xs" style={{ color: 'var(--state-error)' }}>
            {draftError}
          </span>
        ) : null}
      </form>

      {loadError && data.rows.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <div role="alert" style={{ color: 'var(--state-error)' }}>
            {loadError.forbidden
              ? loadError.reason
              : `Could not load the list: ${loadError.reason}`}
          </div>
          {loadError.forbidden ? null : (
            <button
              type="button"
              onClick={() => load(applied)}
              disabled={loading}
              className="mt-3 rounded-md px-3 py-1.5 text-xs font-medium"
              style={PRIMARY_BTN}
            >
              {loading ? 'Retrying…' : 'Retry'}
            </button>
          )}
        </div>
      ) : (
        <>
          <div
            className="px-5 py-3 border-b border-line text-sm"
            role="region"
            aria-label="Not assigned summary"
          >
            <div className="flex flex-wrap gap-2">
              {IGNORED_REASON_ORDER.map((r) => (
                <span
                  key={r}
                  className="inline-block rounded-full bg-state-warning-bg px-2 py-0.5 text-xs font-medium text-state-warning-text"
                >
                  {`${ignoredReasonLabel(r)}: ${byReason[r] || 0}`}
                </span>
              ))}
            </div>
            {byAuthor.length > 0 ? (
              <p className="mt-2 text-xs" style={MUTED}>
                {`By author: ${byAuthor.map((a) => `${a.name || 'unknown'} ${a.n}`).join(', ')}`}
              </p>
            ) : null}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" aria-label="Notes that assigned nobody">
              <thead className="bg-surface-sunken text-content-secondary">
                <tr>
                  {COLS.map((c) => (
                    <th key={c} className="px-4 py-2 text-left font-medium">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 ? (
                  <tr>
                    <td colSpan={COLS.length} className="px-4 py-8 text-center" style={MUTED}>
                      {loading ? 'Loading…' : 'No ignored tags in this range.'}
                    </td>
                  </tr>
                ) : (
                  data.rows.map((r) => (
                    <tr key={r.id} className="border-t border-line">
                      <td className="whitespace-nowrap px-4 py-3 text-left">
                        {formatTs(r.note_created_date)}
                      </td>
                      <td className="px-4 py-3 text-left">{r.student_name || r.student_uuid}</td>
                      <td className="px-4 py-3 text-left">{r.author_name || 'unknown'}</td>
                      <td className="px-4 py-3 text-left">
                        {ignoredRowLabel(r.reason, r.detail, r.detail_name)}
                      </td>
                      <td className="px-4 py-3 text-left">
                        {r.reason === ALREADY_ASSIGNED_REASON ? null : done(r.student_uuid) ? (
                          <span className="text-xs" style={MUTED}>
                            Assigned
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              setAssignTarget({
                                uuid: r.student_uuid,
                                name: r.student_name || r.student_uuid
                              })
                            }
                            aria-label={`Assign advisor to ${r.student_name || r.student_uuid}`}
                            className="rounded-md px-3 py-1.5 text-xs font-medium"
                            style={SECONDARY_BTN}
                          >
                            Assign advisor
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {data.next_cursor > 0 ? (
            <div className="px-5 py-3 border-t border-line">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="rounded-md px-4 py-1.5 text-sm font-medium"
                style={SECONDARY_BTN}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          ) : null}
        </>
      )}
      <AssignAdvisorModal
        student={assignTarget}
        notify={notify}
        onClose={() => setAssignTarget(null)}
        onDone={() => {
          // Covers a 409 too: either way the student now has an advisor.
          if (assignTarget) onAssigned(assignTarget.uuid);
          load(applied);
        }}
      />
    </div>
  );
}

export default IgnoredView;
